import { useState } from 'react';
import { clsx } from 'clsx';
import { Card } from '../common/Card';
import type { RagnarokContext } from '../../types';

interface RagnarokSourcesProps {
  ragnarokContext?: RagnarokContext | null;
}

export function RagnarokSources({ ragnarokContext }: RagnarokSourcesProps) {
  const [showQuery, setShowQuery] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());

  const toggleSource = (index: number) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // No ragnarok_context field at all (older evaluation) — render nothing
  if (ragnarokContext === undefined || ragnarokContext === null) {
    return null;
  }

  // Ragnarok was unavailable during evaluation
  if (!ragnarokContext.available) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <div className="flex items-center gap-2 text-amber-400 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          Company knowledge context was unavailable during this evaluation.
        </div>
      </Card>
    );
  }

  // Available but no results matched
  if (ragnarokContext.sources.length === 0) {
    return (
      <Card className="border-wolf-700/30">
        <p className="text-wolf-500 text-sm">
          No additional company knowledge was found relevant to this project.
        </p>
      </Card>
    );
  }

  // Sources available
  return (
    <Card>
      <div className="space-y-3">
        <div>
          <h4 className="text-wolf-300 font-medium flex items-center gap-2">
            <svg className="w-4 h-4 text-action" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Company Knowledge Referenced
          </h4>
          <p className="text-wolf-500 text-xs mt-1">
            The AI evaluation referenced these internal documents for additional capability context.
          </p>
        </div>

        <div className="space-y-2">
          {ragnarokContext.sources.map((source, index) => (
            <div key={`${source.filename}-${index}`} className="bg-wolf-800/40">
              <button
                onClick={() => source.content_preview && toggleSource(index)}
                className={clsx(
                  'w-full flex items-center justify-between px-3 py-2 text-left',
                  source.content_preview && 'hover:bg-wolf-800/60 transition-colors cursor-pointer'
                )}
              >
                <div className="flex items-center gap-2 min-w-0 mr-3">
                  {source.content_preview && (
                    <svg
                      className={clsx(
                        'w-3 h-3 text-wolf-500 flex-shrink-0 transition-transform',
                        expandedSources.has(index) && 'rotate-90'
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <span className="text-wolf-100 text-sm font-mono truncate">
                    {source.filename}
                  </span>
                </div>
                <span
                  className={clsx(
                    'text-xs font-mono px-2 py-0.5 flex-shrink-0',
                    source.similarity_score >= 0.8
                      ? 'text-action bg-action/10'
                      : source.similarity_score >= 0.6
                        ? 'text-amber-400 bg-amber-400/10'
                        : 'text-wolf-400 bg-wolf-700/50'
                  )}
                >
                  {(source.similarity_score * 100).toFixed(0)}%
                </span>
              </button>
              {expandedSources.has(index) && source.content_preview && (
                <div className="px-3 pb-3 pt-1 border-t border-wolf-700/30">
                  <p className="text-wolf-400 text-xs whitespace-pre-wrap leading-relaxed">
                    {source.content_preview}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {ragnarokContext.query_used && (
          <div>
            <button
              onClick={() => setShowQuery(!showQuery)}
              className="text-wolf-500 text-xs hover:text-wolf-300 transition-colors"
            >
              {showQuery ? 'Hide' : 'Show'} search query
            </button>
            {showQuery && (
              <p className="text-wolf-500 text-xs mt-1 font-mono bg-wolf-800/30 px-2 py-1">
                {ragnarokContext.query_used}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
